/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { yupResolver } from '@hookform/resolvers';

import paths from 'pages/Router/paths';
import { merchantsCleanUp } from 'state/actions/merchants';
import { useFormatDate, useFormatMessage } from 'hooks';
import DatePicker from 'components/DatePicker';
import ErrorMessage from 'components/ErrorMessage';

import './MerchantForm.scss';

const MerchantForm = ({
  isEditing,
  isProfile,
  merchant,
  onSubmitHandler,
  schema,
}) => {
  const { loading, success } = useSelector(
    (state) => ({
      loading: state.merchants.loading,
      success: state.merchants.success,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const { register, handleSubmit, errors, control, watch, setValue } = useForm({
    defaultValues: { ...merchant },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (success) {
      setValue('file', null);
    }
    return () => dispatch(merchantsCleanUp());
  }, [dispatch, success, setValue]);

  const invalidUrlMessage = useFormatMessage('MerchantForm.invalidUrl');

  const imagePreviewUrl =
    watch('file') && watch('file')[0]
      ? URL.createObjectURL(watch('file')[0])
      : merchant.logoUrl;

  const goBackMessage = useFormatMessage('MerchantForm.goBack');
  const pickAnotherFileMessage = useFormatMessage(
    'MerchantForm.pickAnotherFile'
  );
  const pickFileMessage = useFormatMessage('MerchantForm.pickFile');
  const urlMessage = useFormatMessage('MerchantForm.url');
  const activeMessage = useFormatMessage('MerchantForm.active');

  return (
    <>
      <div className="tile is-ancestor">
        <div className="tile is-parent">
          <div className="card tile is-child">
            <header className="card-header">
              <p className="card-header-title">
                <span className="icon">
                  <i className="mdi mdi-account-edit default" />
                </span>
                {useFormatMessage('MerchantForm.merchantInfo')}
              </p>
            </header>
            <div className="card-content">
              <form onSubmit={handleSubmit(onSubmitHandler)}>
                {isEditing ? (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">
                        {useFormatMessage('MerchantForm.name')}
                      </label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <div className="control">
                          <input
                            name="name"
                            id="name"
                            readOnly="readOnly"
                            className="input is-static"
                            ref={register}
                            type="text"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">
                        {useFormatMessage('MerchantForm.name')}
                      </label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <div className="control">
                          <input
                            name="name"
                            id="name"
                            className={classNames('input', {
                              'is-danger': errors.name,
                            })}
                            ref={register}
                            type="text"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {errors.name && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage />
                    </div>
                  </div>
                )}
                <>
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">{urlMessage}</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <div className="control">
                          <input
                            className={classNames(`input`, {
                              'is-danger': errors.url,
                            })}
                            ref={register}
                            name="url"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {errors.url && (
                    <div className="field is-horizontal">
                      <div className="field-label is-normal" />
                      <div className="field-body">
                        <ErrorMessage text={invalidUrlMessage} />
                      </div>
                    </div>
                  )}
                </>

                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('MerchantForm.secret')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="control">
                        <input
                          className={classNames('input', {
                            'is-danger': errors.secret,
                          })}
                          type="text"
                          ref={register}
                          name="secret"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {errors.secret && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage />
                    </div>
                  </div>
                )}

                <div className="field has-check is-horizontal">
                  <div className="field-label">
                    <label className="label">{activeMessage}</label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="field">
                        <div className="control">
                          <label className="b-checkbox checkbox">
                            <input
                              type="checkbox"
                              name="active"
                              ref={register}
                            />
                            <span className="check is-primary" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {errors.active && (
                  <div className="field is-horizontal">
                    <div className="field-label is-normal" />
                    <div className="field-body">
                      <ErrorMessage />
                    </div>
                  </div>
                )}

                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('MerchantForm.created')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <Controller
                        control={control}
                        name="createdAt"
                        render={({ onChange, name, value }) => (
                          <DatePicker
                            name={name}
                            onChange={onChange}
                            date={new Date(value)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <hr />

                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">
                      {useFormatMessage('MerchantForm.logo')}
                    </label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <div className="file has-name">
                        <label className="file-label">
                          <input
                            className="file-input"
                            type="file"
                            name="file"
                            ref={register}
                            accept="image/*"
                          />
                          <span className="file-cta">
                            <span className="file-icon">
                              <i className="mdi mdi-upload" />
                            </span>
                            <span className="file-label">
                              {watch('file') && watch('file').file
                                ? pickAnotherFileMessage
                                : pickFileMessage}
                            </span>
                          </span>
                          <span className="file-name">
                            {watch('file') && watch('file')[0]?.name}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                <div className="field is-horizontal">
                  <div className="field-label" />
                  <div className="field-body">
                    <div className="field">
                      <div className="field is-grouped">
                        <div className="control">
                          <button
                            type="submit"
                            className={`button is-primary ${
                              loading && 'is-loading'
                            }`}
                          >
                            <span>
                              {useFormatMessage('MerchantForm.submit')}
                            </span>
                          </button>
                        </div>
                        {!isProfile && (
                          <Link to={paths.MERCHANTS} className="button">
                            {goBackMessage}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="tile is-parent preview">
          <div className="card tile is-child">
            <header className="card-header">
              <p className="card-header-title">
                <span className="icon">
                  <i className="mdi mdi-account default" />
                </span>
                {useFormatMessage('MerchantForm.merchantPreview')}
              </p>
            </header>
            <div className="card-content">
              {imagePreviewUrl && (
                <>
                  <div className="is-merchant-avatar image has-max-width is-aligned-center">
                    <img
                      className="merchant-avatar"
                      src={imagePreviewUrl}
                      alt="Merchant profile logo preview"
                    />
                  </div>
                  <hr />
                </>
              )}
              <div className="field">
                <label className="label">
                  {useFormatMessage('MerchantForm.name')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="name"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('name')}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">{urlMessage}</label>
                <div className="control is-clearfix">
                  <input
                    data-testid="url"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('url')}
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">
                  {useFormatMessage('MerchantForm.secret')}
                </label>
                <div className="control is-clearfix">
                  <input
                    data-testid="secret"
                    type="text"
                    readOnly="readOnly"
                    className="input is-static"
                    value={watch('secret')}
                  />
                </div>
              </div>
              {!isProfile && (
                <div className="field">
                  <label className="label">{activeMessage}</label>
                  <div className="control is-clearfix" data-testid="active">
                    {watch('active') ? (
                      <span className="icon">
                        <i className="mdi mdi-check" />
                      </span>
                    ) : (
                      <span className="icon">
                        <i className="mdi mdi-close" />
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="field">
                <label className="label">
                  {useFormatMessage('MerchantForm.created')}
                </label>
                <div className="control is-clearfix" data-testid="date">
                  <p className="date">
                    {useFormatDate(watch('createdAt'), {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MerchantForm.propTypes = {
  merchant: PropTypes.shape({
    id: PropTypes.string,
    active: PropTypes.bool,
    name: PropTypes.string,
    secret: PropTypes.string,
    logoUrl: PropTypes.string,
    createdAt: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  isProfile: PropTypes.bool,
};

MerchantForm.defaultProps = {
  isEditing: false,
  isProfile: false,
};

export default MerchantForm;
